/**
 * Follow List Sheet — bottom sheet showing followers or following list.
 * Tappable user rows navigate to their profile.
 */

import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useFollowers, useFollowing } from '@turnifly/shared/hooks'
import { AvatarWithBadge } from '@turnifly/shared/ui'
import { LoadingSpinner } from '@turnifly/shared/ui'
import type { FollowListItem } from '@turnifly/shared/api'

interface FollowListSheetProps {
  isOpen: boolean
  onClose: () => void
  userId: string
  mode: 'followers' | 'following'
}

export const FollowListSheet: React.FC<FollowListSheetProps> = ({
  isOpen, onClose, userId, mode,
}) => {
  const { t } = useTranslation()
  const { data: followers, isLoading: loadingFollowers } = useFollowers(
    mode === 'followers' ? userId : undefined,
  )
  const { data: following, isLoading: loadingFollowing } = useFollowing(
    mode === 'following' ? userId : undefined,
  )

  const list = mode === 'followers' ? followers : following
  const isLoading = mode === 'followers' ? loadingFollowers : loadingFollowing
  const title = mode === 'followers' ? t('profile.followers') : t('profile.following')

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
            className="
              fixed z-50 bg-bg flex flex-col
              inset-x-0 bottom-0 rounded-t-[32px] max-h-[75vh]
              lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2
              lg:rounded-3xl lg:max-h-[70vh] lg:w-full lg:max-w-sm
              lg:border lg:border-white/[0.06] lg:shadow-glass
            "
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
              <button
                onClick={onClose}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/[0.04] tap-highlight-transparent"
              >
                <X size={20} className="text-text" />
              </button>
              <h2 className="font-display font-bold text-lg text-text">{title}</h2>
              <div className="w-10" />
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto px-2 pb-8">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              ) : !list?.length ? (
                <div className="py-12 text-center text-text-hint text-sm">
                  {mode === 'followers'
                    ? t('profile.noFollowersYet')
                    : t('profile.noFollowingYet')}
                </div>
              ) : (
                list.map((user) => (
                  <UserRow key={user.id} user={user} onClose={onClose} />
                ))
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

const UserRow: React.FC<{ user: FollowListItem; onClose: () => void }> = React.memo(
  ({ user, onClose }) => {
    const navigate = useNavigate()

    const handleClick = (): void => {
      onClose()
      navigate(`/profile/${user.id}`)
    }

    return (
      <button
        onClick={handleClick}
        className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl hover:bg-white/[0.03] transition-colors tap-highlight-transparent"
      >
        <AvatarWithBadge
          src={user.avatar_url}
          fallback={user.full_name.charAt(0)}
          size="md"
          badge={user.badge_type as 'premium' | 'expert' | 'company' | null}
        />
        <div className="flex-1 text-left min-w-0">
          <p className="text-sm font-semibold text-text truncate">{user.full_name}</p>
          {user.username && (
            <p className="text-xs text-text-hint">@{user.username}</p>
          )}
        </div>
      </button>
    )
  },
)
UserRow.displayName = 'UserRow'
